import XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const etiquetaDeEtapa = (etapa) =>
  etapa.esFinal ? "OK" : etapa.esDefectuoso ? "SCRAP" : etapa.esRetrabajo ? "RETRABAJO" : (etapa.nombre || "").trim().toUpperCase();

const PESO_CATEGORIA = { OK: 0, SCRAP: 3, RETRABAJO: 2 };

const etapasResultadoDe = (grupo) => {
  const conObjetivo = new Set(
    (grupo.ordenes || []).flatMap((o) => (o.porcentajeEsperadoPorEtapa || []).filter((p) => p.porcentaje !== null && p.porcentaje !== undefined).map((p) => p.etapaId))
  );
  return (grupo.etapas || []).filter((e) => !e.esInicial && (e.esFinal || e.esDefectuoso || e.esRetrabajo || e.tipoObjetivo || conObjetivo.has(e._id)));
};

const redondear = (valor, decimales = 2) => {
  const factor = 10 ** decimales;
  return Math.round((Number(valor) || 0) * factor) / factor;
};

const etiquetaDia = (fecha) => fecha.format("dddd DD/MM").toUpperCase();

export const construirPlan = (grupos = [], { desde, hasta } = {}) => {
  const categorias = [];
  grupos.forEach((grupo) =>
    etapasResultadoDe(grupo).forEach((etapa) => {
      const etiqueta = etiquetaDeEtapa(etapa);
      if (!categorias.includes(etiqueta)) categorias.push(etiqueta);
    })
  );
  categorias.sort((a, b) => (PESO_CATEGORIA[a] ?? 1) - (PESO_CATEGORIA[b] ?? 1));

  const etapasTodas = [];
  grupos.forEach((grupo) =>
    (grupo.etapas || []).forEach((etapa) => {
      const nombre = (etapa.nombre || "").trim();
      if (nombre && !etapasTodas.includes(nombre)) etapasTodas.push(nombre);
    })
  );

  const filas = grupos.flatMap((grupo) => {
    const etiquetaPorEtapa = new Map(etapasResultadoDe(grupo).map((e) => [e._id, etiquetaDeEtapa(e)]));
    const nombrePorEtapa = new Map((grupo.etapas || []).map((e) => [e._id, (e.nombre || "").trim()]));
    const etapaRetrabajo = (grupo.etapas || []).find((e) => e.esRetrabajo);
    return (grupo.ordenes || []).map((orden) => {
      const enEtapa = Object.fromEntries(etapasTodas.map((n) => [n, 0]));
      (orden.avancePorEtapa || []).forEach((a) => {
        const nombre = nombrePorEtapa.get(String(a.etapaId?._id || a.etapaId));
        if (nombre) enEtapa[nombre] = redondear(enEtapa[nombre] + (a.cantidad || 0));
      });
      if (etapaRetrabajo && orden.piezasEnviadasARetrabajo > 0) {
        const nombre = (etapaRetrabajo.nombre || "").trim();
        enEtapa[nombre] = redondear(Math.max(enEtapa[nombre], orden.piezasEnviadasARetrabajo));
      }
      const inicioPlan = orden.planeado?.inicio ? moment(orden.planeado.inicio) : null;
      const finPlan = orden.planeado?.fin ? moment(orden.planeado.fin) : null;
      const inicioReal = orden.avance?.inicioReal ? moment(orden.avance.inicioReal) : null;
      const finReal = inicioReal && orden.terminada && orden.proyectado?.fin ? moment(orden.proyectado.fin) : null;
      const horasPlan = (orden.duracionEstimadaMin || 0) / 60;
      const piezasPorCategoria = Object.fromEntries(categorias.map((c) => [c, 0]));
      (orden.llegadasPorEtapa || []).forEach((llegada) => {
        const etiqueta = etiquetaPorEtapa.get(llegada.etapaId);
        if (etiqueta) piezasPorCategoria[etiqueta] = redondear(piezasPorCategoria[etiqueta] + (llegada.llegaron || 0));
      });
      const piezasOk = piezasPorCategoria.OK || 0;
      const cantidad = orden.cantidadEsperada || 0;
      const faltante = redondear(Math.max(0, cantidad - piezasOk));
      const completa = faltante === 0;
      const referencia = orden.terminada && orden.fechaTerminada ? moment(orden.fechaTerminada) : moment();
      const atrasada = !!finPlan && referencia.isAfter(finPlan) && (orden.terminada || !completa);
      const diasAtraso = atrasada ? referencia.clone().startOf("day").diff(finPlan.clone().startOf("day"), "days") : 0;
      const matricesOrden = orden.matricesResumen || [];
      const totalRequerido = matricesOrden.reduce((acc, m) => acc + (m.cantidadRequerida || 0), 0);
      const atribucion = matricesOrden.length
        ? matricesOrden.map((m) => ({
            clave: m.clave || "",
            descripcion: m.descripcion || "",
            parte: totalRequerido > 0 ? (m.cantidadRequerida || 0) / totalRequerido : 1 / matricesOrden.length,
          }))
        : [{ clave: "", descripcion: orden.partidaSnapshot?.nombre || "Sin matriz", parte: 1 }];
      return {
        flujo: grupo.nombre,
        enEtapa,
        atribucion,
        piezasOk,
        faltante,
        completa,
        atrasada,
        diasAtraso,
        pctPiezas: cantidad > 0 ? Math.min(100, redondear((piezasOk / cantidad) * 100, 1)) : 0,
        folio: orden.folio,
        descripcion: (orden.matricesResumen || []).map((m) => m.descripcion).filter(Boolean).join(", ") || orden.partidaSnapshot?.nombre || "",
        noParte: [...new Set((orden.matricesResumen || []).map((m) => m.clave).filter(Boolean))].join(", "),
        cantidad: orden.cantidadEsperada || 0,
        inicioPlan,
        finPlan,
        horasPlan: redondear(horasPlan),
        piezasPorHora: horasPlan > 0 ? redondear((orden.cantidadEsperada || 0) / horasPlan, 1) : null,
        inicioReal,
        finReal,
        horasReal: inicioReal && finReal ? redondear(finReal.diff(inicioReal, "minutes", true) / 60) : null,
        estado: orden.terminada ? (completa ? "Terminada" : "Incompleta") : inicioReal ? "En proceso" : "Pendiente",
        piezasPorCategoria,
      };
    });
  });

  filas.sort((a, b) => (a.inicioPlan ? a.inicioPlan.valueOf() : 0) - (b.inicioPlan ? b.inicioPlan.valueOf() : 0));
  const dias = [];
  const rangoLargo = desde && hasta && moment(hasta).diff(moment(desde), "days") > 8;
  if (desde && hasta && !rangoLargo) {
    for (let fecha = moment(desde).startOf("day"); fecha.isSameOrBefore(moment(hasta), "day"); fecha.add(1, "day")) {
      dias.push({ clave: fecha.format("YYYY-MM-DD"), etiqueta: etiquetaDia(fecha), filas: [] });
    }
  }
  filas.forEach((fila) => {
    const clave = fila.inicioPlan ? fila.inicioPlan.format("YYYY-MM-DD") : "sin-fecha";
    let dia = dias.find((d) => d.clave === clave);
    if (!dia) {
      dia = { clave, etiqueta: fila.inicioPlan ? etiquetaDia(fila.inicioPlan) : "SIN FECHA", filas: [] };
      dias.push(dia);
    }
    dia.filas.push(fila);
  });
  dias.sort((a, b) => (a.clave === "sin-fecha" ? 1 : b.clave === "sin-fecha" ? -1 : a.clave.localeCompare(b.clave)));
  dias.forEach((dia) => {
    dia.horasPlan = redondear(dia.filas.reduce((acc, f) => acc + f.horasPlan, 0));
    dia.horasReal = redondear(dia.filas.reduce((acc, f) => acc + (f.horasReal || 0), 0));
    dia.cantidad = dia.filas.reduce((acc, f) => acc + f.cantidad, 0);
    Object.assign(dia, resumirFilas(dia.filas));
  });
  const todasLasFilas = dias.flatMap((d) => d.filas);
  return { categorias, dias, resumen: resumirFilas(todasLasFilas), porMatriz: resumirPorMatriz(todasLasFilas, etapasTodas), etapasTodas };
};

const resumirPorMatriz = (filas, etapasTodas = []) => {
  const acumulado = new Map();
  filas.forEach((fila) =>
    fila.atribucion.forEach(({ clave, descripcion, parte }) => {
      const llave = `${clave}|${descripcion}`;
      const m = acumulado.get(llave) || { clave, descripcion, ordenes: 0, planeadas: 0, ok: 0, scrap: 0, retrabajo: 0, incompletas: 0, atrasadas: 0, porEtapa: Object.fromEntries(etapasTodas.map((n) => [n, 0])) };
      etapasTodas.forEach((n) => {
        m.porEtapa[n] += (fila.enEtapa[n] || 0) * parte;
      });
      m.ordenes += 1;
      m.planeadas += fila.cantidad * parte;
      m.ok += fila.piezasOk * parte;
      m.scrap += (fila.piezasPorCategoria.SCRAP || 0) * parte;
      m.retrabajo += (fila.piezasPorCategoria.RETRABAJO || 0) * parte;
      if (!fila.completa) m.incompletas += 1;
      if (fila.atrasada) m.atrasadas += 1;
      acumulado.set(llave, m);
    })
  );
  return [...acumulado.values()]
    .map((m) => {
      const planeadas = redondear(m.planeadas);
      const ok = redondear(m.ok);
      return {
        ...m,
        porEtapa: Object.fromEntries(Object.entries(m.porEtapa).map(([n, v]) => [n, redondear(v)])),
        planeadas,
        ok,
        scrap: redondear(m.scrap),
        retrabajo: redondear(m.retrabajo),
        faltante: redondear(Math.max(0, planeadas - ok)),
        pctCumplimiento: planeadas > 0 ? Math.min(100, redondear((ok / planeadas) * 100, 1)) : 0,
      };
    })
    .sort((a, b) => a.pctCumplimiento - b.pctCumplimiento || b.faltante - a.faltante);
};

export const COLUMNAS_POR_MATRIZ = [
  { titulo: "Número de parte", ancho: 18 },
  { titulo: "Matriz", ancho: 36 },
  { titulo: "Órdenes", ancho: 9 },
  { titulo: "Planeadas", ancho: 11 },
  { titulo: "Terminadas (OK)", ancho: 13 },
  { titulo: "Scrap", ancho: 9 },
  { titulo: "Retrabajo", ancho: 10 },
  { titulo: "Faltante", ancho: 10 },
  { titulo: "% cumplimiento", ancho: 13 },
  { titulo: "Órdenes incompletas", ancho: 13 },
  { titulo: "Órdenes atrasadas", ancho: 13 },
];

export const encabezadosPorEtapa = (etapasTodas) => ["Número de parte", "Matriz", "Planeadas", ...etapasTodas, "Faltante", "% cumplimiento"];

export const valoresPorEtapa = (m, etapasTodas) => [m.clave, m.descripcion, m.planeadas, ...etapasTodas.map((n) => m.porEtapa[n] || 0), m.faltante, `${m.pctCumplimiento}%`];

export const totalPorMatriz = (porMatriz, etapasTodas) => {
  const suma = (campo) => redondear(porMatriz.reduce((acc, m) => acc + m[campo], 0));
  const planeadas = suma("planeadas");
  const ok = suma("ok");
  return {
    clave: "",
    descripcion: "TOTAL",
    ordenes: porMatriz.reduce((acc, m) => acc + m.ordenes, 0),
    planeadas,
    ok,
    scrap: suma("scrap"),
    retrabajo: suma("retrabajo"),
    incompletas: porMatriz.reduce((acc, m) => acc + m.incompletas, 0),
    atrasadas: porMatriz.reduce((acc, m) => acc + m.atrasadas, 0),
    porEtapa: Object.fromEntries(etapasTodas.map((n) => [n, redondear(porMatriz.reduce((acc, m) => acc + (m.porEtapa[n] || 0), 0))])),
    faltante: redondear(Math.max(0, planeadas - ok)),
    pctCumplimiento: planeadas > 0 ? Math.min(100, redondear((ok / planeadas) * 100, 1)) : 0,
  };
};

export const valoresPorMatriz = (m) => [m.clave, m.descripcion, m.ordenes, m.planeadas, m.ok, m.scrap, m.retrabajo, m.faltante, `${m.pctCumplimiento}%`, m.incompletas, m.atrasadas];

const resumirFilas = (filas) => {
  const cantidad = filas.reduce((acc, f) => acc + f.cantidad, 0);
  const piezasOk = redondear(filas.reduce((acc, f) => acc + f.piezasOk, 0));
  const ordenes = filas.length;
  const ordenesCompletas = filas.filter((f) => f.completa).length;
  return {
    piezasOk,
    faltante: redondear(filas.reduce((acc, f) => acc + f.faltante, 0)),
    pctPiezas: cantidad > 0 ? Math.min(100, redondear((piezasOk / cantidad) * 100, 1)) : 0,
    ordenes,
    ordenesCompletas,
    ordenesAtrasadas: filas.filter((f) => f.atrasada).length,
    pctOrdenes: ordenes > 0 ? redondear((ordenesCompletas / ordenes) * 100, 1) : 0,
  };
};

export const COLUMNAS_CUMPLIMIENTO = [
  { titulo: "Faltante", ancho: 9 },
  { titulo: "% piezas", ancho: 9 },
  { titulo: "Atraso (días)", ancho: 10 },
];

export const textoAtraso = (fila) => (fila.atrasada ? (fila.diasAtraso > 0 ? fila.diasAtraso : "<1") : "");

export const filasResumen = (resumen) => [
  ["Órdenes planeadas", resumen.ordenes],
  ["Órdenes completas", resumen.ordenesCompletas],
  ["Órdenes atrasadas", resumen.ordenesAtrasadas],
  ["% cumplimiento por órdenes", `${resumen.pctOrdenes}%`],
  ["Piezas terminadas (OK)", resumen.piezasOk],
  ["Piezas faltantes", resumen.faltante],
  ["% cumplimiento por piezas", `${resumen.pctPiezas}%`],
];

export const COLUMNAS_PLAN = [
  { titulo: "Día", ancho: 16 },
  { titulo: "Folio", ancho: 9 },
  { titulo: "Descripción", ancho: 34 },
  { titulo: "Número de parte", ancho: 18 },
  { titulo: "Inicio", ancho: 9 },
  { titulo: "Término", ancho: 9 },
  { titulo: "Cantidad", ancho: 10 },
  { titulo: "Pzs/hora", ancho: 9 },
  { titulo: "Horas", ancho: 8 },
  { titulo: "Inicio real", ancho: 11 },
  { titulo: "Fin real", ancho: 11 },
  { titulo: "Horas reales", ancho: 9 },
  { titulo: "Estado", ancho: 11 },
];

export const horaCorta = (fecha, diaBase) => {
  if (!fecha) return "";
  return diaBase && !fecha.isSame(diaBase, "day") ? fecha.format("DD/MM HH:mm") : fecha.format("HH:mm");
};

const borde = { style: "thin", color: { rgb: "000000" } };
const bordes = { top: borde, bottom: borde, left: borde, right: borde };
const estilo = ({ fondo, negrita, alinear = "center", tam = 10, color } = {}) => ({
  font: { name: "Calibri", sz: tam, bold: !!negrita, ...(color ? { color: { rgb: color } } : {}) },
  alignment: { horizontal: alinear, vertical: "center", wrapText: true },
  border: bordes,
  ...(fondo ? { fill: { fgColor: { rgb: fondo } } } : {}),
});
const celda = (v, s) => ({ v: v === null || v === undefined ? "" : v, t: typeof v === "number" ? "n" : "s", s });

export const SIN_ORDENES = "Sin órdenes planeadas";

export const descargarPlanProduccionExcel = ({ grupos, desde, hasta, empresaNombre, etiquetaRango, nombreArchivo }) => {
  const { categorias, dias, resumen, porMatriz, etapasTodas } = construirPlan(grupos, { desde, hasta });
  const columnas = [...COLUMNAS_PLAN, ...categorias.map((c) => ({ titulo: c, ancho: 10 })), ...COLUMNAS_CUMPLIMIENTO];
  const total = columnas.length;
  const vacios = (n, s) => Array.from({ length: n }, () => celda("", s));
  const sTitulo = estilo({ negrita: true, tam: 14 });
  const sEncabezado = estilo({ fondo: "C5E3F6", negrita: true });
  const sDia = estilo({ fondo: "FFF2CC", negrita: true });
  const sTexto = estilo({ alinear: "left" });
  const sNumero = estilo();
  const sTotal = estilo({ fondo: "EDEDED", negrita: true });

  const aoa = [
    [celda(empresaNombre || "", sTitulo), ...vacios(total - 1, sTitulo)],
    [celda("PLAN DE PRODUCCIÓN", sTitulo), ...vacios(total - 3, sTitulo), celda("MPP-FT-06", estilo({ negrita: true })), celda("", estilo()), celda("", estilo())],
    [celda(etiquetaRango || "", estilo({ negrita: true, alinear: "left" })), ...vacios(total - 1, estilo({ alinear: "left" }))],
    columnas.map((c) => celda(c.titulo, sEncabezado)),
  ];
  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: total - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: total - 4 } },
    { s: { r: 1, c: total - 3 }, e: { r: 1, c: total - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: total - 1 } },
  ];

  dias.forEach((dia) => {
    const filaInicio = aoa.length;
    if (dia.filas.length === 0) {
      aoa.push([celda(dia.etiqueta, sDia), celda(SIN_ORDENES, estilo({ alinear: "left", color: "8C8C8C" })), ...vacios(total - 2, sNumero)]);
      merges.push({ s: { r: filaInicio, c: 1 }, e: { r: filaInicio, c: total - 1 } });
      return;
    }
    dia.filas.forEach((fila, indice) => {
      const base = fila.inicioPlan;
      aoa.push([
        celda(indice === 0 ? dia.etiqueta : "", sDia),
        celda(fila.folio, sNumero),
        celda(fila.descripcion, sTexto),
        celda(fila.noParte, sNumero),
        celda(horaCorta(fila.inicioPlan), sNumero),
        celda(horaCorta(fila.finPlan, base), sNumero),
        celda(fila.cantidad, sNumero),
        celda(fila.piezasPorHora ?? "", sNumero),
        celda(fila.horasPlan, sNumero),
        celda(horaCorta(fila.inicioReal, base), sNumero),
        celda(horaCorta(fila.finReal, base), sNumero),
        celda(fila.horasReal ?? "", sNumero),
        celda(fila.estado, sNumero),
        ...categorias.map((c) => celda(fila.piezasPorCategoria[c] || 0, sNumero)),
        celda(fila.faltante, sNumero),
        celda(`${fila.pctPiezas}%`, sNumero),
        celda(textoAtraso(fila), fila.atrasada ? estilo({ color: "C00000", negrita: true }) : sNumero),
      ]);
    });
    if (dia.filas.length > 1) merges.push({ s: { r: filaInicio, c: 0 }, e: { r: filaInicio + dia.filas.length - 1, c: 0 } });
    aoa.push([
      celda("", sTotal),
      celda("", sTotal),
      celda(`TOTAL ${dia.etiqueta}`, estilo({ fondo: "EDEDED", negrita: true, alinear: "left" })),
      ...vacios(3, sTotal),
      celda(dia.cantidad, sTotal),
      celda("", sTotal),
      celda(dia.horasPlan, sTotal),
      celda("", sTotal),
      celda("", sTotal),
      celda(dia.horasReal || "", sTotal),
      celda("", sTotal),
      ...categorias.map((c) => celda(dia.filas.reduce((acc, f) => acc + (f.piezasPorCategoria[c] || 0), 0), sTotal)),
      celda(dia.faltante, sTotal),
      celda(`${dia.pctPiezas}%`, sTotal),
      celda(dia.ordenesAtrasadas ? `${dia.ordenesAtrasadas} atrasada(s)` : "", sTotal),
    ]);
  });

  if (dias.some((d) => d.filas.length > 0)) {
    aoa.push(vacios(total, estilo({ alinear: "left" })));
    const filaTitulo = aoa.length;
    aoa.push([celda("RESUMEN DEL PERIODO", sEncabezado), ...vacios(3, sEncabezado)]);
    merges.push({ s: { r: filaTitulo, c: 0 }, e: { r: filaTitulo, c: 3 } });
    filasResumen(resumen).forEach(([etiqueta, valor]) => {
      const fila = aoa.length;
      aoa.push([celda(etiqueta, sTotal), ...vacios(2, sTotal), celda(valor, sTotal)]);
      merges.push({ s: { r: fila, c: 0 }, e: { r: fila, c: 2 } });
    });
  }

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = columnas.map((c) => ({ wch: c.ancho }));
  hoja["!merges"] = merges;
  hoja["!rows"] = [{ hpt: 22 }, { hpt: 22 }];
  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Plan de produccion");

  if (porMatriz.length > 0) {
    const filaTotal = totalPorMatriz(porMatriz, etapasTodas);
    const anchoMatriz = COLUMNAS_POR_MATRIZ.length;
    const aoaMatriz = [
      [celda(empresaNombre || "", sTitulo), ...vacios(anchoMatriz - 1, sTitulo)],
      [celda("CUMPLIMIENTO POR MATRIZ", sTitulo), ...vacios(anchoMatriz - 1, sTitulo)],
      [celda(etiquetaRango || "", estilo({ negrita: true, alinear: "left" })), ...vacios(anchoMatriz - 1, estilo({ alinear: "left" }))],
      COLUMNAS_POR_MATRIZ.map((c) => celda(c.titulo, sEncabezado)),
      ...porMatriz.map((m) =>
        valoresPorMatriz(m).map((v, i) => celda(v, i === 1 ? sTexto : i === 8 && m.pctCumplimiento < 100 ? estilo({ negrita: true, color: m.pctCumplimiento < 50 ? "C00000" : "9C5700" }) : sNumero))
      ),
      valoresPorMatriz(filaTotal).map((v) => celda(v, sTotal)),
    ];
    const hojaMatriz = XLSXStyle.utils.aoa_to_sheet(aoaMatriz);
    hojaMatriz["!cols"] = COLUMNAS_POR_MATRIZ.map((c) => ({ wch: c.ancho }));
    hojaMatriz["!merges"] = [0, 1, 2].map((r) => ({ s: { r, c: 0 }, e: { r, c: anchoMatriz - 1 } }));
    XLSXStyle.utils.book_append_sheet(libro, hojaMatriz, "Cumplimiento por matriz");

    const encabezados = encabezadosPorEtapa(etapasTodas);
    const anchoEtapas = encabezados.length;
    const aoaEtapas = [
      [celda(empresaNombre || "", sTitulo), ...vacios(anchoEtapas - 1, sTitulo)],
      [celda("PIEZAS POR ETAPA Y MATRIZ (dónde está cada pieza hoy)", sTitulo), ...vacios(anchoEtapas - 1, sTitulo)],
      [celda(etiquetaRango || "", estilo({ negrita: true, alinear: "left" })), ...vacios(anchoEtapas - 1, estilo({ alinear: "left" }))],
      encabezados.map((t) => celda(t, sEncabezado)),
      ...porMatriz.map((m) => valoresPorEtapa(m, etapasTodas).map((v, i) => celda(v, i === 1 ? sTexto : sNumero))),
      valoresPorEtapa(filaTotal, etapasTodas).map((v) => celda(v, sTotal)),
    ];
    const hojaEtapas = XLSXStyle.utils.aoa_to_sheet(aoaEtapas);
    hojaEtapas["!cols"] = encabezados.map((_, i) => ({ wch: i === 0 ? 18 : i === 1 ? 36 : 12 }));
    hojaEtapas["!merges"] = [0, 1, 2].map((r) => ({ s: { r, c: 0 }, e: { r, c: anchoEtapas - 1 } }));
    XLSXStyle.utils.book_append_sheet(libro, hojaEtapas, "Piezas por etapa");
  }
  XLSXStyle.writeFile(libro, `${nombreArchivo}.xlsx`);
};
